import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Award, Crown, Sparkles } from "lucide-react";
import { LOGO, HERO_SLIDES, HERO_COLLAGE, PARTNER_MARQUEE, SERVICES, PORTFOLIO_IMAGES } from "../lib/content";
import { api } from "../lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 0.61, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

function HeroRotator() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % HERO_SLIDES.length), 7000);
    return () => clearInterval(id);
  }, []);
  const slide = HERO_SLIDES[i];

  return (
    <>
      {/* Background image — one per tagline */}
      <AnimatePresence mode="sync">
        <motion.div
          key={`bg-${i}`}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 0.55, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${slide.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.15) contrast(1.05)",
          }}
        />
      </AnimatePresence>

      {/* Tagline */}
      <div className="relative min-h-[1.2em]" data-testid="hero-tagline-rotator">
        <AnimatePresence mode="wait">
          <motion.div
            key={`t-${i}`}
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -32, opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
            className="font-serif text-[clamp(2.4rem,6vw,5.5rem)] leading-[1.05] tracking-tight"
          >
            {slide.tagline}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Slide indicator dots */}
      <div className="mt-10 flex items-center gap-2" data-testid="hero-slide-dots">
        {HERO_SLIDES.map((_, k) => (
          <button
            key={k}
            onClick={() => setI(k)}
            className={`h-px transition-all ${k === i ? "w-14 bg-[var(--crest-gold)]" : "w-7 bg-[var(--crest-cream)]/30 hover:bg-[var(--crest-cream)]/60"}`}
            aria-label={`Slide ${k + 1}`}
            data-testid={`hero-dot-${k}`}
          />
        ))}
      </div>
    </>
  );
}

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 3))).catch(() => {});
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const portfolioFallback = PORTFOLIO_IMAGES.map((src, i) => ({
    _id: `f-${i}`,
    title: ["Vision Campaign", "Editorial Direction", "Executive Identity", "Conference Production", "Brand Architecture", "Public Image Refresh"][i],
    client: ["Atlas Foundation", "Sovereign Media", "Coastal Executive", "Hampton Roads Summit", "Pinnacle Group", "Public Figure"][i],
    category: ["campaign", "creative_direction", "branding", "conference", "branding", "creative_direction"][i],
    cover_image_url: src,
  }));
  const portfolioToShow = portfolio.length ? portfolio : portfolioFallback;

  return (
    <div className="bg-[var(--crest-cream)]" data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[var(--crest-black)] text-[var(--crest-cream)] on-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/65 to-black z-10 pointer-events-none" />

        <div className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-24 md:pt-44 md:pb-32 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="eyebrow mb-8"
              style={{ color: "var(--crest-gold)" }}
            >
              —  Luxury Branding · Consulting · Production
            </motion.div>
            <HeroRotator />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-10 max-w-2xl text-[1.05rem] md:text-lg leading-relaxed text-[var(--crest-cream)]/80"
            >
              Upper Crest Image is a luxury branding, marketing, and consulting firm
              helping organizations, public figures, productions, and corporations elevate
              their presence, messaging, and operations. Editorial taste. Strategic discipline.
              National reach with a Hampton Roads heart.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/book" className="btn-primary" data-testid="hero-book-cta" style={{ background: "var(--crest-gold)", color: "var(--crest-black)", borderColor: "var(--crest-gold)" }}>
                Book Consultation <ArrowRight size={14} />
              </Link>
              <Link to="/portfolio" className="btn-secondary" data-testid="hero-portfolio-cta">View Portfolio</Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, delay: 0.3 }}
            className="md:col-span-4 hidden md:block"
          >
            <div className="grid grid-cols-2 gap-4 ml-auto max-w-md">
              {HERO_COLLAGE.map((src, idx) => (
                <div key={idx} className={`overflow-hidden ${idx % 3 === 0 ? "translate-y-6" : ""}`}>
                  <img src={src} alt="" loading="lazy" className="luxe-img w-full h-44 object-cover" />
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Partner Marquee */}
        <div className="relative z-20 border-t border-[var(--crest-cream)]/10 py-5 overflow-hidden bg-black/40">
          <div className="flex marquee-track gap-12 whitespace-nowrap text-[0.7rem] tracking-[0.34em] uppercase text-[var(--crest-cream)]/50">
            {[...PARTNER_MARQUEE, ...PARTNER_MARQUEE].map((p, i) => (
              <span key={i} className="flex items-center gap-12">
                <span>{p}</span>
                <span style={{ color: "var(--crest-gold)" }}>◆</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={stagger}
        className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-12"
      >
        <motion.div variants={fadeUp} className="md:col-span-4 eyebrow text-[var(--crest-muted)]">— The Studio</motion.div>
        <div className="md:col-span-8">
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-5xl leading-[1.08] tracking-tight">
            We don't dress brands. We <span className="shimmer-gold">position</span> them
            for the rooms they were built to lead.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-8 text-lg leading-relaxed text-[var(--crest-muted)] max-w-3xl">
            Public figures. Politicians. Non-profits. Corporations. Influencers. Productions.
            Campaigns. Each engagement is engineered around the principal — and every
            decision is filtered through one question: does this elevate the position?
          </motion.p>
        </div>
      </motion.section>

      {/* SERVICES */}
      <section className="bg-white border-y border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="md:col-span-4 sticky md:top-24 h-fit"
          >
            <motion.div variants={fadeUp} className="eyebrow text-[var(--crest-gold)]">— What We Do</motion.div>
            <motion.h3 variants={fadeUp} className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Services</motion.h3>
            <motion.p variants={fadeUp} className="mt-6 text-[var(--crest-muted)] text-base leading-relaxed">
              From identity to influence — a complete arsenal for the principal ready to lead.
              Every package is customized following an initial consultation.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8">
              <Link to="/services" className="btn-ghost-gold inline-flex" data-testid="home-services-explore">
                Explore Services <ArrowRight size={14} />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={stagger}
            className="md:col-span-8 grid md:grid-cols-2 gap-px bg-black/10"
          >
            {SERVICES.slice(0, 8).map((s) => (
              <motion.div
                key={s.id}
                variants={fadeUp}
                className="bg-white p-8 hover:bg-[var(--crest-cream-soft)] transition-colors"
                data-testid={`home-service-${s.id}`}
              >
                <div className="flex items-center justify-end mb-4">
                  <Sparkles size={14} style={{ color: "var(--crest-gold)" }} />
                </div>
                <h4 className="font-serif text-2xl tracking-tight">{s.title}</h4>
                <p className="text-sm text-[var(--crest-muted)] mt-3 leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        variants={stagger}
        className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32"
      >
        <div className="flex items-end justify-between mb-12">
          <motion.div variants={fadeUp}>
            <div className="eyebrow text-[var(--crest-gold)]">— Selected Work</div>
            <h3 className="font-serif text-4xl md:text-5xl tracking-tight mt-3">Recent Engagements</h3>
          </motion.div>
          <motion.div variants={fadeUp}>
            <Link to="/portfolio" className="btn-ghost-gold hidden md:inline-flex" data-testid="home-portfolio-link">
              View All <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {portfolioToShow.map((p, i) => (
            <motion.div key={p._id || i} variants={fadeUp} className={i === 1 ? "md:translate-y-12" : ""}>
              <Link to="/portfolio" className="group block" data-testid={`home-portfolio-item-${i}`}>
                <div className="overflow-hidden bg-black aspect-[3/4]">
                  <img src={p.cover_image_url} alt={p.title} loading="lazy" className="luxe-img w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="pt-5">
                  <div className="eyebrow text-[var(--crest-gold)]">{(p.category || "").replace(/_/g, " ")}</div>
                  <div className="font-serif text-xl mt-2 gold-underline inline-block">{p.title}</div>
                  <div className="text-sm text-[var(--crest-muted)] mt-2">{p.client}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* TESTIMONIALS */}
      <section className="bg-[var(--crest-black)] text-[var(--crest-cream)] on-dark">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-12"
        >
          <motion.div variants={fadeUp} className="md:col-span-4">
            <div className="eyebrow" style={{ color: "var(--crest-gold)" }}>— On the Record</div>
            <h3 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Words from the Principals</h3>
            <p className="text-[var(--crest-cream)]/65 mt-6 max-w-md">
              Trust earned in boardrooms, on campaign trails, and at black-tie podiums.
            </p>
            <Link to="/testimonials" className="btn-ghost-gold mt-8 inline-flex" data-testid="home-testimonials-link">
              All Testimonials <ArrowRight size={14} />
            </Link>
          </motion.div>
          <div className="md:col-span-8 grid md:grid-cols-2 gap-6">
            {(testimonials.length ? testimonials : [
              { _id: "t1", client_name: "L. Bennett", client_title: "Executive Director", organization: "Atlas Foundation", quote: "Upper Crest Image transformed how the world receives our mission. The clarity, the polish — flawless." },
              { _id: "t2", client_name: "Rev. M. Carter", client_title: "Senior Pastor", organization: "Sovereign Sanctuary", quote: "I came to them for image consulting. I left with a complete strategic framework for influence." },
            ]).map((t) => (
              <motion.div
                key={t._id}
                variants={fadeUp}
                className="border border-[var(--crest-cream)]/15 p-8 hover:border-[var(--crest-gold)]/60 transition-colors"
                data-testid={`home-testimonial-${t._id}`}
              >
                <Award size={18} style={{ color: "var(--crest-gold)" }} />
                <p className="font-serif text-xl md:text-2xl leading-snug mt-5">"{t.quote}"</p>
                <div className="mt-8">
                  <div className="font-medium">{t.client_name}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[var(--crest-cream)]/55 mt-1">
                    {t.client_title}{t.organization ? ` · ${t.organization}` : ""}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={stagger}
        className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center"
      >
        <motion.div variants={fadeUp}><Crown size={28} style={{ color: "var(--crest-gold)" }} className="mx-auto" /></motion.div>
        <motion.h3 variants={fadeUp} className="font-serif text-4xl md:text-6xl tracking-tight mt-6 max-w-3xl mx-auto leading-[1.05]">
          Ready to position the exceptional?
        </motion.h3>
        <motion.p variants={fadeUp} className="text-[var(--crest-muted)] mt-6 max-w-xl mx-auto">
          Begin with a consultation. Apply your deposit toward the final engagement.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-10 flex justify-center gap-4 flex-wrap">
          <Link to="/book" className="btn-primary" data-testid="home-final-cta">Book Consultation</Link>
          <Link to="/contact" className="btn-secondary" data-testid="home-contact-cta">Send an Inquiry</Link>
        </motion.div>
      </motion.section>
    </div>
  );
}
